import { useEffect, useState } from 'react';

import { readVisibleDidYouKnowEntries } from '@api/instant_survey';
import { useAppSelector } from '@redux/hooks';
import { Badge, Card, ChevronLeftIcon, ChevronRightIcon, IconButtonV2 } from '@utilities';

import type { DidYouKnowEntry } from '@api/instant_survey/didYouKnow';
import styles from './_index.module.scss';

const AUTO_ADVANCE_INTERVAL_MS = 5000;

const DidYouKnow = () => {
  const panelAbbrev = useAppSelector((state) => state.user.data.panel.abbrev);
  const [entries, setEntries] = useState<DidYouKnowEntry[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (panelAbbrev !== 'US') return;

    const controller = new AbortController();
    readVisibleDidYouKnowEntries({ signal: controller.signal })
      .then((results) => {
        setEntries(results);
        setActiveIndex(0);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setEntries([]);
          setActiveIndex(0);
        }
      });

    return () => controller.abort();
  }, [panelAbbrev]);

  useEffect(() => {
    if (entries.length < 2) return;

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % entries.length);
    }, AUTO_ADVANCE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [entries.length]);

  if (panelAbbrev !== 'US' || entries.length === 0) return null;

  const entry = entries[activeIndex];
  const learnMoreUrl = entry.learnMoreUrl;

  return (
    <Card aria-label="Did You Know" as="aside" className={styles['did-you-know']} elevation={1}>
      <div className={styles['did-you-know-top']}>
        <h2>Did You Know?</h2>
        <Badge
          className={styles['did-you-know-tag']}
          label={entry.isLinked ? 'Highlight' : 'New'}
        />
      </div>
      <p className={styles['did-you-know-title']}>{entry.title}</p>
      <h3 className={styles['did-you-know-headline']}>{entry.headline}</h3>
      <p className={styles['did-you-know-description']}>{entry.description}</p>
      {learnMoreUrl && (
        <a
          className={styles['did-you-know-link']}
          href={learnMoreUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          Learn more ↗
        </a>
      )}
      {entries.length > 1 && (
        <div className={styles['did-you-know-controls']}>
          <IconButtonV2
            aria-label="Previous entry"
            icon={<ChevronLeftIcon />}
            onClick={() => setActiveIndex((activeIndex - 1 + entries.length) % entries.length)}
          />
          <span aria-live="polite">
            {activeIndex + 1} of {entries.length}
          </span>
          <IconButtonV2
            aria-label="Next entry"
            icon={<ChevronRightIcon />}
            onClick={() => setActiveIndex((activeIndex + 1) % entries.length)}
          />
        </div>
      )}
    </Card>
  );
};

export default DidYouKnow;
